import {
  Badge,
  Button,
  DateRangePicker,
  Dialog,
  Icon,
  IconSizeClasses,
  QuickSelectOption,
  Tooltip,
} from '@eppendorf/vnls-react-components';
import { subDays, subMonths } from 'date-fns';
import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCreateAuditExport } from '../audit-export.api';

import './export-audit-events-dialog.scss';
import { ReportGenerationInProgressDialog } from './report-generation-in-progress-dialog';

export interface ExportAuditEventsDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ExportAuditEventsDialog({
  isOpen,
  onOpenChange,
}: ExportAuditEventsDialogProps): ReactElement {
  const { t } = useTranslation();
  const [dateRange, setDateRange] = useState<{
    from?: Date;
    to?: Date;
  }>({});
  const [selectedQuickOption, setSelectedQuickOption] = useState<
    QuickSelectOption | undefined
  >(undefined);
  const [isInProgressDialogOpen, setIsInProgressDialogOpen] = useState(false);

  const createAuditExportMutation = useCreateAuditExport();

  const disabledDates = {
    before: subDays(new Date(), 89),
    after: new Date(),
  };

  const handleGenerateReport = async () => {
    if (!dateRange.from || !dateRange.to) {
      return;
    }

    try {
      await createAuditExportMutation.mutateAsync({
        startDate: dateRange.from.toISOString(),
        endDate: dateRange.to.toISOString(),
        reportType: 'AuditTrail',
      });

      // Close dialog after successful request
      onOpenChange(false);

      // Show in-progress dialog
      setIsInProgressDialogOpen(true);
    } catch (error) {
      // TODO: Show error notification to user
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  const handleDateRangeChange = (range: { from?: Date; to?: Date } | undefined) => {
    setDateRange(range || {});
    // Reset quick select when manually changing date range
    setSelectedQuickOption(undefined);
  };

  const handleQuickSelectOption = (option: QuickSelectOption) => {
    setSelectedQuickOption(option);
  };

  const isGenerateDisabled =
    !dateRange.from || !dateRange.to || createAuditExportMutation.isPending;

  return (
    <>
      <Dialog
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        className="export-audit-dialog w-max-4xl"
        hideCloseButton
      >
        <div className="flex flex__dir--column flex__ai--center text-align-center">
          <div className="flex flex__ai--center flex__jc--center m-bottom-l">
            <Icon
              name="document-zip"
              size={IconSizeClasses.Large}
              className="bg-blue-500"
            />
          </div>
          <Dialog.Title className="m-bottom-m font-weight-semibold">
            <div style={{ overflow: 'visible' }}>
              <span
                style={{ fontSize: '24px' }}
                className="flex flex__ai--center flex__jc--center gap-s"
              >
                {t('auditEvents.export.title', 'Export audit data')}
                <Tooltip
                  trigger={
                    <div style={{ display: 'inline-block' }}>
                      <Badge
                        color="purple"
                        size="small"
                        className="flex flex__ai--center gap-xs bg-purple-500 color-white font-weight-bold"
                      >
                        <Icon
                          name="flag"
                          size={IconSizeClasses.XXSmall}
                          className="bg-white"
                        />
                        {t('reportHub.beta.badge')}
                      </Badge>
                    </div>
                  }
                >
                  {t(
                    'reportHub.beta.tooltip',
                    'Early access: this feature is in beta until beginning of 2027 and may change or be removed. Your feedback helps shape VisioNize Lab Suite.',
                  )}
                </Tooltip>
              </span>
            </div>
          </Dialog.Title>
          <div
            className="m-bottom-l text-gray-600 font-size-m text-align-left"
            style={{ lineHeight: '1.5' }}
          >
            A ZIP archive will be generated for the selected time frame that may contain
            one or more PDF reports. Once ready, it will be available for download in the{' '}
            <span className="font-weight-bold">Report hub</span>.
          </div>

          <div className="export-audit-dialog-datepicker w-full flex flex__jc--flex-start">
            <DateRangePicker
              dateRange={
                dateRange.from && dateRange.to
                  ? { from: dateRange.from, to: dateRange.to }
                  : undefined
              }
              onDateRangeChange={handleDateRangeChange}
              disabled={disabledDates}
              startMonth={disabledDates.before}
              endMonth={disabledDates.after}
              defaultMonth={subMonths(disabledDates.after, 1)}
              centered
              quickSelect={{
                enabled: true,
                onOptionSelect: handleQuickSelectOption,
                selectedOption: selectedQuickOption,
              }}
            />
          </div>
        </div>

        <Dialog.Actions className="m-top-xxl">
          <Button variant="secondary" onClick={handleCancel}>
            {t('shared.cancel', 'Cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleGenerateReport}
            disabled={isGenerateDisabled}
          >
            {createAuditExportMutation.isPending
              ? t('shared.generating', 'Generating...')
              : t('auditEvents.export.generateReport', 'Generate report')}
          </Button>
        </Dialog.Actions>
      </Dialog>
      <ReportGenerationInProgressDialog
        isOpen={isInProgressDialogOpen}
        onOpenChange={setIsInProgressDialogOpen}
      />
    </>
  );
}
